import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { AdminReviewItem } from '@gg/shared';
import { DocumentPreview } from '@/components/DocumentPreview';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Notice,
  TextAreaField,
  TextField,
} from '@/components/ui';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/dates';
import { useAdminReviewQueue, useReviewDocument, useReviewLicence } from '@/lib/queries';

const keyOf = (i: AdminReviewItem) => `${i.kind}:${i.id}`;
const day = (d: string) => formatDate(`${d}T12:00:00Z`);

/**
 * A-05 · Document review queue: certificates and licences waiting for review, oldest first. The
 * selected item shows its inline preview (certificate) or its number (licence), with Approve and
 * Reject with a reason. A rejection emails the participant (Brevo, via the queue) and puts them
 * back in Documents missing; an approval takes them out.
 */
export function AdminReview() {
  const { t } = useTranslation();
  const queue = useAdminReviewQueue();
  const [params, setParams] = useSearchParams();
  const items = queue.data?.items ?? [];
  const selected = items.find((i) => keyOf(i) === params.get('item')) ?? items[0];
  const [flash, setFlash] = useState<string | null>(null);

  const select = (item: AdminReviewItem | undefined) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (item) next.set('item', keyOf(item));
        else next.delete('item');
        return next;
      },
      { replace: true },
    );

  /** After a decision: the next item of the queue, the flash message. */
  const done = (message: string, reviewed: AdminReviewItem) => {
    const at = items.findIndex((i) => keyOf(i) === keyOf(reviewed));
    select(items[at + 1] ?? items[at - 1]);
    setFlash(message);
  };

  return (
    <section className="space-y-6">
      <div className="flex items-baseline justify-between gap-4">
        <h1 className="font-display text-section-lg font-bold">{t('admin.review.title')}</h1>
        {queue.data && (
          <span className="text-sm text-ink-muted">
            {t('admin.review.count', { count: items.length })}
          </span>
        )}
      </div>
      <Notice>{t('admin.review.intro')}</Notice>
      {queue.data && !queue.data.rejectionEmail && (
        <Notice variant="outline">{t('admin.review.noRejectionEmail')}</Notice>
      )}
      {flash && (
        <p role="status" className="text-sm font-semibold text-success">
          {flash}
        </p>
      )}
      {queue.isError && (
        <p role="alert" className="text-sm text-danger">
          {t('admin.review.error')}
        </p>
      )}
      {queue.data && items.length === 0 && (
        <p className="py-8 text-center text-ink-muted">{t('admin.review.empty')}</p>
      )}

      {selected && (
        <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
          <ul
            className="m-0 flex list-none flex-col gap-2 p-0"
            aria-label={t('admin.review.title')}
          >
            {items.map((i) => {
              const current = keyOf(i) === keyOf(selected);
              return (
                <li key={keyOf(i)}>
                  <button
                    type="button"
                    aria-current={current || undefined}
                    onClick={() => {
                      setFlash(null);
                      select(i);
                    }}
                    className={`flex w-full flex-col gap-1 rounded-control border p-3 text-left ${
                      current ? 'border-ink bg-surface' : 'border-line bg-paper'
                    }`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="font-semibold">{personName(i)}</span>
                      <Badge>{t(`admin.review.kind.${i.kind}`)}</Badge>
                    </span>
                    <span className="text-helper text-ink-muted">
                      {t('admin.review.submittedAt', { date: formatDateTime(i.submittedAt) })}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <ReviewPanel key={keyOf(selected)} item={selected} onDone={done} />
        </div>
      )}
    </section>
  );
}

const personName = (i: AdminReviewItem) =>
  [i.participant.firstName, i.participant.lastName].filter(Boolean).join(' ') ||
  i.participant.email;

function ReviewPanel({
  item,
  onDone,
}: {
  item: AdminReviewItem;
  onDone: (message: string, item: AdminReviewItem) => void;
}) {
  const { t } = useTranslation();
  const reviewDocument = useReviewDocument();
  const reviewLicence = useReviewLicence();
  const pending = reviewDocument.isPending || reviewLicence.isPending;
  const [expiresOn, setExpiresOn] = useState(
    item.kind === 'certificate' ? (item.certificate.expiresOn ?? '') : '',
  );
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setError(null), [rejecting]);

  const decide = async (
    decision: { status: 'approved' } | { status: 'rejected'; reason: string },
  ) => {
    setError(null);
    try {
      if (item.kind === 'certificate') {
        const body =
          decision.status === 'approved'
            ? {
                status: 'approved' as const,
                ...(expiresOn && expiresOn !== item.certificate.expiresOn && { expiresOn }),
              }
            : decision;
        await reviewDocument.mutateAsync({ id: item.id, body });
      } else {
        await reviewLicence.mutateAsync({ userId: item.id, body: decision });
      }
      setRejecting(false);
      onDone(
        t(decision.status === 'approved' ? 'admin.review.approved' : 'admin.review.rejected', {
          name: personName(item),
        }),
        item,
      );
    } catch (e) {
      const code = e instanceof ApiRequestError ? e.code : '';
      setError(
        code === 'not_pending_review'
          ? t('admin.review.conflict')
          : code === 'expiry_required'
            ? t('admin.review.expiryRequired')
            : t('admin.review.failed'),
      );
    }
  };

  const p = item.participant;
  return (
    <Card className="gap-4!">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="m-0 font-display text-section font-bold">{personName(item)}</h2>
          <span className="text-sm text-ink-muted">
            {[p.email, p.birthDate && t('admin.review.born', { date: day(p.birthDate) })]
              .filter(Boolean)
              .join(' · ')}
          </span>
          <span className="text-sm text-ink-muted">{p.formats.join(', ')}</span>
        </div>
        <Link to={`/admin/registrations/${p.registrationId}`} className="text-sm font-semibold">
          {t('admin.review.registration')}
        </Link>
      </div>

      {item.kind === 'certificate' ? (
        <>
          <dl className="m-0 grid gap-1 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-ink-muted">{t('admin.review.issuedOn')}</dt>
              <dd className="m-0 font-semibold">
                {item.certificate.issuedOn ? day(item.certificate.issuedOn) : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-ink-muted">{t('admin.review.file')}</dt>
              <dd className="m-0 font-semibold break-all">{item.certificate.fileName}</dd>
            </div>
          </dl>
          <DocumentPreview documentId={item.id} fileName={item.certificate.fileName} />
          <div className="max-w-xs">
            <TextField
              type="date"
              label={t('admin.review.expiresOn')}
              hint={t('admin.review.expiresOnHint')}
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
            />
          </div>
        </>
      ) : (
        <>
          <dl className="m-0 grid gap-1 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-ink-muted">{t('admin.review.federation')}</dt>
              <dd className="m-0 font-semibold">{item.licence.federation}</dd>
            </div>
            <div>
              <dt className="text-ink-muted">{t('admin.review.number')}</dt>
              <dd className="m-0 font-semibold">{item.licence.number}</dd>
            </div>
          </dl>
          <Notice>{t('admin.review.licenceHint')}</Notice>
        </>
      )}

      {error && (
        <p role="alert" className="m-0 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <Button
          size="md"
          className="w-auto!"
          loading={pending && !rejecting}
          disabled={item.kind === 'certificate' && !expiresOn}
          onClick={() => decide({ status: 'approved' })}
        >
          {t('admin.review.approve')}
        </Button>
        <Button
          variant="secondary"
          size="md"
          className="w-auto!"
          disabled={pending}
          onClick={() => setRejecting(true)}
        >
          {t('admin.review.reject')}
        </Button>
      </div>

      <ConfirmDialog
        open={rejecting}
        title={t('admin.review.rejectTitle')}
        confirmLabel={t('admin.review.reject')}
        cancelLabel={t('admin.review.cancel')}
        loading={pending}
        onCancel={() => setRejecting(false)}
        onConfirm={() => {
          if (!reason.trim()) return setError(t('admin.review.reasonRequired'));
          void decide({ status: 'rejected', reason: reason.trim() });
        }}
      >
        <div className="flex flex-col gap-3">
          <TextAreaField
            label={t('admin.review.reason')}
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            error={rejecting ? (error ?? undefined) : undefined}
          />
          <span className="text-helper text-ink-muted">{t('admin.review.reasonHint')}</span>
        </div>
      </ConfirmDialog>
    </Card>
  );
}
